import gsap from 'gsap';
import { getBoxesSequence } from './infinite-floating-boxes/getBoxesSequence';
import inputTracker from './infinite-floating-boxes/inputTracker';

// Change GAP to control spacing between belt elements (px).
const GAP = 120;
const SPEED = 2; // px per frame (desktop)
const INPUT_SCALE = 60; // wheel / touch speed multiplier
const MOBILE_GAP = 24;
const MOBILE_SPEED = 1; // px per frame (mobile)
const MOBILE_TITLE_PRE_GAP = 80; // extra gap before text elements in vs layout

// Horizontal arc amplitude (px) applied at viewport edges for depth illusion.
const PARALLAX_AMP = 80;

const RESUME_DELAY_MS = 200; // pause after opposite-direction scroll
const RESUME_RAMP_MS = 400; // ramp-up duration after the pause

export default function infiniteFloatingBoxes() {
  let elements: HTMLElement[] = [];
  let cumulativeOffsets: number[] = [];
  let elementSizes: number[] = [];
  let depthFactors: number[] = [];
  let beltSize = 0;
  let offset = 0;
  let isVertical = false;
  let isPaused = false;
  let lastOppositeScrollAt = 0;

  const InputTracker = inputTracker({ lerpFactor: 0.1 });

  return { init, play, pause };

  function init(boxes: HTMLElement[]) {
    elements = boxes;
    isVertical = window.innerWidth < window.innerHeight;

    setup();

    gsap.ticker.fps(60);
    gsap.ticker.add(tick);

    boxes.forEach((el) => {
      if (el.hasAttribute('data-floating-link')) {
        el.addEventListener('click', () => {
          isPaused = true;
          el.classList.add('-in-transition');
        });
      }
    });

    useOnWindowFinishResize(onResize);
  }

  function setup() {
    applyScales();
    measureBelt();
    setInitialOffset();
    renderFrame();
  }

  function applyScales() {
    depthFactors = new Array(elements.length).fill(0);
    let projectIndex = 0;
    elements.forEach((el, i) => {
      if (isTitleEl(el)) {
        scaleTitleEl(el);
      } else {
        depthFactors[i] = scaleProjectEl(el, projectIndex++);
      }
    });
  }

  function measureBelt() {
    const gap = isVertical ? MOBILE_GAP : GAP;
    let cum = 0;
    cumulativeOffsets = [];
    elementSizes = [];
    elements.forEach((el, i) => {
      if (isVertical && i > 0 && el.hasAttribute('data-floating-heading')) {
        cum += MOBILE_TITLE_PRE_GAP;
      }
      cumulativeOffsets.push(cum);
      const size = isVertical ? el.offsetHeight : el.offsetWidth;
      elementSizes.push(size);
      cum += size + gap;
    });
    beltSize = cum;
    // Extra gap before title-heading at the wrap point.
    if (isVertical && elements.length > 0 && elements[0].hasAttribute('data-floating-heading')) {
      beltSize += MOBILE_TITLE_PRE_GAP;
    }
  }

  function setInitialOffset() {
    if (elements.length < 1) {
      offset = 0;
    } else if (isVertical) {
      // Start first element ~20% from the top of the screen.
      offset = window.innerHeight * 0.67;
    } else {
      // Center title-heading (index 0) in the viewport on load.
      const vw = window.innerWidth;
      offset = vw / 2 + cumulativeOffsets[0] + elementSizes[0] / 2;
    }
  }

  function tick() {
    if (isPaused) {
      return;
    }
    const inputSpeed = InputTracker.getCurrentSpeed();
    const speed = isVertical ? MOBILE_SPEED : SPEED;
    const now = Date.now();

    if (inputSpeed.preferred < -0.01) {
      lastOppositeScrollAt = now;
    }

    const elapsed = now - lastOppositeScrollAt;
    let autoSpeed: number;
    if (elapsed < RESUME_DELAY_MS) {
      autoSpeed = 0;
    } else if (elapsed < RESUME_DELAY_MS + RESUME_RAMP_MS) {
      autoSpeed = (speed * (elapsed - RESUME_DELAY_MS)) / RESUME_RAMP_MS;
    } else {
      autoSpeed = speed;
    }

    offset += autoSpeed + inputSpeed.preferred * INPUT_SCALE;
    renderFrame();
  }

  function renderFrame() {
    if (beltSize === 0) {
      return;
    }
    const viewport = isVertical ? window.innerHeight : window.innerWidth;
    const wrapped = ((offset % beltSize) + beltSize) % beltSize;

    elements.forEach((el, i) => {
      let pos = viewport + cumulativeOffsets[i] - wrapped;

      if (isVertical) {
        if (pos + elementSizes[i] < 0) {
          pos += beltSize;
        } else if (pos > viewport) {
          const prev = pos - beltSize;
          if (prev + elementSizes[i] > 0) {
            pos = prev;
          }
        }
      } else if (pos + elementSizes[i] < -PARALLAX_AMP) {
        // Exited left — wrap to the right.
        pos += beltSize;
      } else if (pos > viewport + PARALLAX_AMP) {
        // Far off right: use previous-loop position if it's in range.
        const prev = pos - beltSize;
        if (prev + elementSizes[i] > -PARALLAX_AMP) {
          pos = prev;
        }
      }

      if (isVertical) {
        gsap.set(el, { x: '1.5rem', y: pos });
      } else {
        const center = pos + elementSizes[i] / 2;
        const xOffset =
          depthFactors[i] * PARALLAX_AMP * (center / viewport - 0.5);
        const renderedPos = pos + xOffset;
        // Hide element if still fully outside the viewport.
        const visible =
          renderedPos + elementSizes[i] > 0 && renderedPos < viewport;
        gsap.set(el, {
          x: renderedPos,
          y: 0,
          visibility: visible ? 'visible' : 'hidden'
        });
      }
    });
  }

  function isTitleEl(el: HTMLElement) {
    return (
      el.hasAttribute('data-floating-text') ||
      el.hasAttribute('data-floating-heading')
    );
  }

  function scaleTitleEl(el: HTMLElement) {
    if (isVertical) {
      el.style.width = 'calc(100% - 3rem)';
      el.style.height = 'auto';
      el.style.top = '';
      el.style.bottom = '';
    } else {
      el.style.width = '';
      el.style.height = '';
      const rowPx = window.innerWidth < 800 ? 8 : 10;
      if (el.hasAttribute('data-floating-text')) {
        el.style.top = `${rowPx}svh`;
        el.style.bottom = '';
      } else {
        el.style.top = '';
        el.style.bottom = `${rowPx}svh`;
      }
    }
  }

  // Returns the depth factor for the element: +1 foreground, −1 background.
  function scaleProjectEl(el: HTMLElement, projectIndex: number): number {
    const sequence = getBoxesSequence();
    const format = sequence[projectIndex % sequence.length];

    if (isVertical) {
      el.style.width = 'calc(100% - 3rem)';
      el.style.height = 'auto';
      el.style.top = '';
      el.style.bottom = '';
    } else {
      el.style.width = `${format.height * format.aspectRatio}svh`;
      el.style.height = `${format.height}svh`;
      const rowPx = window.innerWidth < 800 ? 8 : 10;
      if (format.row < 0) {
        el.style.top = '';
        el.style.bottom = `${Math.abs(format.row) * rowPx}svh`;
      } else {
        el.style.bottom = '';
        el.style.top = `${format.row * rowPx}svh`;
      }
      el.setAttribute('data-transition', format.row < 3 ? 'up' : 'down');
    }

    // Bottom row = foreground (+1). Top row = background (−1).
    return format.row < 0 ? 1 : -1;
  }

  function onResize() {
    isVertical = window.innerWidth < window.innerHeight;
    applyScales();
    measureBelt();
    renderFrame();
  }

  function play() {
    isPaused = false;
  }

  function pause() {
    isPaused = true;
  }
}

function useOnWindowFinishResize(cb: () => void) {
  let t: ReturnType<typeof setTimeout>;
  window.addEventListener('resize', () => {
    clearTimeout(t);
    t = setTimeout(cb, 500);
  });
}
